// NOTE: Always run `eslint --fix` and `prettier --write` before committing changes to this file.
// This file must conform to Mora's strict linting and formatting rules.
import { type DataDeletionReport, describeDataDeletion } from '@shared/disconnect-data-deletion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { useEffectiveWorkspaceId } from '@/hooks/useEffectiveWorkspaceId';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import {
  PERSISTED_CONNECTION_PLATFORM_IDS,
  isPersistedConnectionPlatform,
} from '@/lib/platforms/connection-capabilities';
import { getPlatformLabel } from '@/lib/platforms/identity';
import { ServiceError } from '@/services/base/ServiceError';
import { linkedInAdsConnectionEnabled } from '@/services/socialAuth/linkedinScopes';
import type { PlatformType } from '@/services/socialAuth/types';
import {
  type FacebookConnectionData,
  type GoogleAdsConnectionData,
  type GoogleAnalyticsConnectionData,
  type InstagramConnectionData,
  type LinkedInAdsConnectionData,
  type LinkedInConnectionData,
  type MetaAdsConnectionData,
  type PinterestConnectionData,
  type ShopifyConnectionData,
  type TikTokConnectionData,
  type YouTubeConnectionData,
  extractConnectionData,
} from '@/types/oauth-vault-integration';
import { logger } from '@/utils/logger';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast';

type UserConnectionRow = Database['public']['Tables']['user_connections']['Row'];
type RawAccountData = UserConnectionRow | Record<string, unknown>;

function getStoredConnectionEmail(connection: UserConnectionRow): string | null {
  const metadata = connection.metadata as Record<string, unknown> | null;
  const connectionData = connection.connection_data as Record<string, unknown> | null;
  const value = [
    metadata?.email,
    metadata?.account_email,
    connectionData?.email,
    connectionData?.account_email,
  ].find(
    (candidate): candidate is string => typeof candidate === 'string' && candidate.includes('@'),
  );
  return value ?? null;
}

export interface SelectedIntegrationResource {
  id: string;
  resourceType?: string;
  providerResourceId: string;
  displayName: string;
  parentDisplayName: string | null;
  currencyCode: string | null;
  isManager: boolean | null;
  lastSyncedAt: string | null;
  syncStatus?: 'pending' | 'syncing' | 'ready' | 'empty' | 'blocked' | 'failed' | 'stale';
  lastError?: string | null;
  lastCheckedAt?: string | null;
  accountType?: 'person' | 'organization' | null;
  authorUrn?: string | null;
  identityState?: string | null;
  imageUrl?: string | null;
  /**
   * `provider_metadata->>'direction'` — Slack's only consumer today
   * ('ingest' | 'send' | 'both'). `null` for every other platform's rows,
   * which don't write this key. Kept optional-shaped (not required) so
   * Google/Meta call sites reading this interface are unaffected.
   */
  direction?: string | null;
}

export interface ConnectedAccount {
  accountId: string;
  platform: string;
  displayName?: string;
  identifier?: string;
  imageUrl: string | null;
  profileUrl: string | null;
  status: string;
  description: string | null;
  rawData: RawAccountData;
  connected: boolean;
  error: string | null;
  icon: string;
  accountInfo: RawAccountData | null;
  lastSync: string | null;
  followerCount?: number | null;
  followingCount?: number | null;
  tokenExpiresAt?: string | null;
  selectedResources?: SelectedIntegrationResource[];
  /**
   * Distinguishes an established resource ledger with zero selected rows from
   * a legacy connection that has never created ledger rows. This prevents a
   * deliberate "select none" choice from falling back to every synced account.
   */
  resourceSelectionMode?: 'selected' | 'none-selected' | 'legacy' | 'unavailable';
}

interface UsePlatformIntegrationsReturn {
  connectedAccounts: ConnectedAccount[];
  availablePlatforms: PlatformType[];
  /**
   * 🔴 NOT a general-purpose "connect this platform" call. This is a bare
   * `initiateAuth` — it opens the OAuth popup/redirect with NO listener for
   * the result. Phase B (`oauth-finalize-with-account`) only runs from
   * `useOAuthConnection`'s window "message" listener, which only
   * `IntegrationConnectManager` mounts, which only renders inside Settings.
   * Call this from anywhere else and the popup completes with the provider,
   * then posts its result into a void: no modal, no error, no saved
   * connection — a silent, 100%-reproducible no-op. This has already had to
   * be patched away from PlatformPlugMenu, PlatformDisconnectedView, and
   * IntegrationContext.connectPlatform (2026-09-15). Do not add a new
   * caller: dispatch `window.dispatchEvent(new CustomEvent('mora:openSettings',
   * { detail: { section: 'integrations', highlight: platform } }))` instead
   * (see any of those three files, or AnalyticsFiltersPopover), or reach for
   * `IntegrationConnectManager`'s `api.startOAuth` if you're already inside
   * one.
   */
  disconnectFunction: (platform: PlatformType, accountId: string) => Promise<void>;
  isLoading: boolean;
  isDisconnecting: Record<string, boolean>;
  refreshFunction: () => void;
  /**
   * Surfaces a failure in either of the two queries feeding this hook: the
   * `current_account_owner_id()` resolution query (takes precedence, since
   * its failure blocks the connections query from ever running — see
   * BUG-INTEGRATIONS-TEAM-001 eng-review gap) or the connections fetch
   * itself. `null` when both queries are healthy (including the normal
   * "no accounts connected yet" empty state).
   */
  error: string | null;
  connectedPlatforms: string[];
  hasShopifyConnected: boolean;
  hasFacebookConnected: boolean;
  hasInstagramConnected: boolean;
  hasLinkedInConnected: boolean;
  hasTikTokConnected: boolean;
  hasYouTubeConnected: boolean;
  hasPinterestConnected: boolean;
  /** The workspace `connectedAccounts` is scoped to, or `null` before it has
   *  resolved. See the return statement's own docblock for why this is
   *  exposed rather than left implicit. */
  workspaceId: string | null;
}

const ALL_PLATFORMS = PERSISTED_CONNECTION_PLATFORM_IDS;

const QUERY_KEY = ['platform-integrations'] as const;

function isAuthLockStealError(err: unknown): boolean {
  if (!err) return false;
  const message =
    typeof err === 'object' && 'message' in err
      ? String((err as { message: unknown }).message ?? '')
      : '';
  return (
    message.includes("'steal' option") ||
    message.includes('Lock broken by another request') ||
    message.includes('Lock "lock:sb-')
  );
}

export function sanitizeAvatarUrl(url: string | null, userId?: string): string | null {
  if (!url) return null;

  // The retired project's binaries were only partially migrated. A blind host
  // rewrite manufactures a current-project 404 for missing objects.
  if (url.includes('xytglhiqbicrnryljyem.supabase.co')) {
    return null;
  }

  // If it's already a valid absolute HTTP/HTTPS URL and does not start with protocol-relative '//' that lacks a domain:
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname;
      if (
        (hostname.startsWith('linkedin_') ||
          hostname.startsWith('tiktok_') ||
          hostname.startsWith('instagram_') ||
          hostname.startsWith('facebook_')) &&
        !hostname.includes('.')
      ) {
        const filename = hostname + parsed.pathname;
        const bucketPath = userId ? `${userId}/${filename}` : filename;
        return `https://apegsjpotzvxumdedwty.supabase.co/storage/v1/object/public/avatars/${bucketPath}`;
      }
    } catch {
      // Ignore and proceed
    }
    return url;
  }

  if (url.startsWith('//')) {
    const withoutSlash = url.slice(2);
    if (
      withoutSlash.startsWith('linkedin_') ||
      withoutSlash.startsWith('tiktok_') ||
      withoutSlash.startsWith('instagram_') ||
      withoutSlash.startsWith('facebook_')
    ) {
      const bucketPath = userId ? `${userId}/${withoutSlash}` : withoutSlash;
      return `https://apegsjpotzvxumdedwty.supabase.co/storage/v1/object/public/avatars/${bucketPath}`;
    }
    return `https:${url}`;
  }

  if (
    url.startsWith('linkedin_') ||
    url.startsWith('tiktok_') ||
    url.startsWith('instagram_') ||
    url.startsWith('facebook_')
  ) {
    const bucketPath = userId ? `${userId}/${url}` : url;
    return `https://apegsjpotzvxumdedwty.supabase.co/storage/v1/object/public/avatars/${bucketPath}`;
  }

  if (url.includes('/') && !url.startsWith('/')) {
    const parts = url.split('/');
    const lastPart = parts[parts.length - 1];
    if (
      lastPart.startsWith('linkedin_') ||
      lastPart.startsWith('tiktok_') ||
      lastPart.startsWith('instagram_') ||
      lastPart.startsWith('facebook_')
    ) {
      return `https://apegsjpotzvxumdedwty.supabase.co/storage/v1/object/public/avatars/${url}`;
    }
  }

  return url;
}

export function getLinkedInAvatarUrl(
  connection: UserConnectionRow,
  liData: LinkedInConnectionData,
  userId: string,
): string | null {
  return sanitizeAvatarUrl(
    connection.profile_image_url || liData.profile_picture_url || liData.logo_url || null,
    userId,
  );
}

/**
 * `brandKitId` scopes the read to one workspace. `user_connections.brand_kit_id`
 * is NOT NULL as of 2026-08-22 — every connection belongs to exactly one
 * workspace (see the app-sidebar.tsx comment this hook's caller supersedes).
 * `brandKitId` defaults to `null`/omitted only for direct callers (existing
 * unit tests exercising this pure function) — every real code path resolves
 * and passes the active workspace before calling this.
 */
export async function fetchConnectedAccountsFromDb(
  ownerId: string,
  brandKitId?: string | null,
): Promise<ConnectedAccount[]> {
  let queryBuilder = supabase
    .from('user_connections')
    .select('*')
    .eq('user_id', ownerId)
    .eq('is_active', true);
  if (brandKitId) {
    queryBuilder = queryBuilder.eq('brand_kit_id', brandKitId);
  }
  const { data: connections, error } = await queryBuilder;

  if (error) {
    if (isAuthLockStealError(error)) {
      // Transient Supabase auth-js lock contention; let TanStack Query retry.
      throw Object.assign(new Error('auth_lock_steal'), { cause: error });
    }
    throw new Error(error.message);
  }

  if (!Array.isArray(connections)) return [];

  // The resource ledger migration rolls out independently from the Settings
  // code. A missing/unavailable ledger must not hide established connections;
  // the primary connection row remains the truthful fallback until it exists.
  const { data: resourceData, error: resourceError } = await supabase
    .from('integration_selected_resources')
    .select(
      'id, connection_id, resource_type, provider_resource_id, display_name, parent_display_name, currency_code, is_manager, last_synced_at, sync_status, last_error, last_checked_at, is_selected, provider_metadata',
    )
    .eq('owner_id', ownerId);
  if (resourceError && import.meta.env.DEV) {
    logger.warn('[PlatformIntegrations] selected-resource ledger unavailable', resourceError);
  }
  const selectedResourcesByConnection = new Map<string, SelectedIntegrationResource[]>();
  const ledgerConnections = new Set<string>();
  for (const resource of (resourceData ?? []) as unknown as Array<{
    id: string;
    connection_id: string;
    resource_type: string;
    provider_resource_id: string;
    display_name: string;
    parent_display_name: string | null;
    currency_code: string | null;
    is_manager: boolean | null;
    last_synced_at: string | null;
    sync_status?: SelectedIntegrationResource['syncStatus'];
    last_error?: string | null;
    last_checked_at?: string | null;
    is_selected: boolean;
    provider_metadata: Record<string, unknown> | null;
  }>) {
    ledgerConnections.add(resource.connection_id);
    if (!resource.is_selected) continue;
    const resources = selectedResourcesByConnection.get(resource.connection_id) ?? [];
    resources.push({
      id: resource.id,
      resourceType: resource.resource_type,
      providerResourceId: resource.provider_resource_id,
      displayName: resource.display_name,
      parentDisplayName: resource.parent_display_name,
      currencyCode: resource.currency_code,
      isManager: resource.is_manager,
      lastSyncedAt: resource.last_synced_at,
      syncStatus: resource.sync_status,
      lastError: resource.last_error,
      lastCheckedAt: resource.last_checked_at,
      accountType:
        resource.provider_metadata?.account_type === 'person' ||
        resource.provider_metadata?.account_type === 'organization'
          ? resource.provider_metadata.account_type
          : null,
      authorUrn:
        typeof resource.provider_metadata?.author_urn === 'string'
          ? resource.provider_metadata.author_urn
          : null,
      identityState:
        typeof resource.provider_metadata?.identity_state === 'string'
          ? resource.provider_metadata.identity_state
          : null,
      imageUrl:
        typeof resource.provider_metadata?.profile_image_url === 'string'
          ? resource.provider_metadata.profile_image_url
          : typeof resource.provider_metadata?.profile_picture_url === 'string'
            ? resource.provider_metadata.profile_picture_url
            : null,
      direction:
        typeof resource.provider_metadata?.direction === 'string'
          ? (resource.provider_metadata.direction as string)
          : null,
    });
    selectedResourcesByConnection.set(resource.connection_id, resources);
  }

  const accounts: ConnectedAccount[] = [];
  for (const connection of connections) {
    try {
      const { platform, platform_user_id, last_sync, id } = connection;
      if (!isPersistedConnectionPlatform(platform)) continue;

      const metadataExpiry = (connection.metadata as Record<string, unknown> | null)?.[
        'token_expires_at'
      ];
      const tokenExpiresAt =
        connection.token_expires_at ?? (typeof metadataExpiry === 'string' ? metadataExpiry : null);
      const base: Pick<
        ConnectedAccount,
        | 'accountId'
        | 'status'
        | 'connected'
        | 'error'
        | 'rawData'
        | 'accountInfo'
        | 'lastSync'
        | 'followerCount'
        | 'followingCount'
        | 'tokenExpiresAt'
        | 'selectedResources'
        | 'resourceSelectionMode'
      > = {
        accountId: id,
        status: 'active' as const,
        connected: true as const,
        // Surface backend connection health (e.g. 'token_invalid' from the
        // refresh_connection_token_health probe) so the UI can show "Reconnect".
        error: connection.last_error ?? null,
        rawData: connection,
        accountInfo: connection,
        lastSync: last_sync,
        followerCount: connection.follower_count,
        followingCount: connection.following_count,
        tokenExpiresAt,
        selectedResources: selectedResourcesByConnection.get(id) ?? [],
        resourceSelectionMode: resourceError
          ? 'unavailable'
          : ledgerConnections.has(id)
            ? (selectedResourcesByConnection.get(id)?.length ?? 0) > 0
              ? 'selected'
              : 'none-selected'
            : 'legacy',
      };

      switch (platform) {
        case 'shopify': {
          const shopifyData = extractConnectionData<ShopifyConnectionData>(connection, 'shopify');
          accounts.push({
            ...base,
            platform: 'shopify',
            displayName:
              connection.display_name ||
              shopifyData.shop_name ||
              shopifyData.myshopify_domain ||
              platform_user_id,
            identifier:
              connection.platform_username || shopifyData.myshopify_domain || platform_user_id,
            imageUrl: sanitizeAvatarUrl(connection.profile_image_url || null, ownerId),
            profileUrl: shopifyData.domain
              ? `https://${shopifyData.domain}`
              : `https://${platform_user_id}`,
            description: `Shopify Store: ${connection.display_name || shopifyData.shop_name || platform_user_id}`,
            icon: 'shopify',
          });
          break;
        }
        case 'facebook': {
          const fbData = extractConnectionData<FacebookConnectionData>(connection, 'facebook');
          accounts.push({
            ...base,
            platform: 'facebook',
            displayName: connection.display_name || fbData.page_name || platform_user_id,
            identifier: connection.platform_username || fbData.page_name || platform_user_id,
            imageUrl: sanitizeAvatarUrl(
              connection.profile_image_url ||
                fbData.picture_url ||
                fbData.profile_picture_url ||
                null,
              ownerId,
            ),
            profileUrl: fbData.page_id ? `https://facebook.com/${fbData.page_id}` : null,
            description: connection.display_name || fbData.page_name || platform_user_id,
            icon: 'facebook',
          });
          break;
        }
        case 'instagram': {
          const igData = extractConnectionData<InstagramConnectionData>(connection, 'instagram');
          const username = connection.platform_username || igData.username;
          accounts.push({
            ...base,
            platform: 'instagram',
            displayName:
              connection.display_name || (username ? `@${username}` : 'Instagram Account'),
            identifier: username,
            imageUrl: sanitizeAvatarUrl(
              connection.profile_image_url || igData.profile_picture_url || null,
              ownerId,
            ),
            profileUrl: username ? `https://instagram.com/${username}` : null,
            description: igData.biography || null,
            icon: 'instagram',
          });
          break;
        }
        case 'linkedin': {
          const liData = extractConnectionData<LinkedInConnectionData>(connection, 'linkedin');
          const accountType = connection.account_type;
          const vanityName = liData.vanity_name?.trim();
          accounts.push({
            ...base,
            platform: 'linkedin',
            displayName: connection.display_name || liData.organization_name || 'LinkedIn Account',
            identifier: connection.platform_username || platform_user_id,
            imageUrl: getLinkedInAvatarUrl(connection, liData, ownerId),
            profileUrl:
              accountType === 'organization'
                ? `https://linkedin.com/company/${vanityName || platform_user_id}`
                : vanityName
                  ? `https://linkedin.com/in/${vanityName}`
                  : null,
            description: liData.description || null,
            icon: 'linkedin',
          });
          break;
        }
        case 'linkedin-ads': {
          const adsData = extractConnectionData<LinkedInAdsConnectionData>(
            connection,
            'linkedin-ads',
          );
          accounts.push({
            ...base,
            platform: 'linkedin-ads',
            displayName: connection.display_name || adsData.ad_account_name || 'LinkedIn Ads',
            identifier: connection.platform_username || adsData.ad_account_id || platform_user_id,
            imageUrl: null,
            profileUrl: null,
            description: adsData.currency
              ? `LinkedIn Ads · ${adsData.currency}`
              : 'LinkedIn advertising account',
            icon: 'linkedin-ads',
          });
          break;
        }
        case 'tiktok': {
          const tkData = extractConnectionData<TikTokConnectionData>(connection, 'tiktok');
          const username = connection.platform_username || tkData.username || platform_user_id;
          accounts.push({
            ...base,
            platform: 'tiktok',
            displayName: connection.display_name || tkData.display_name || username,
            identifier: username,
            imageUrl: sanitizeAvatarUrl(
              connection.profile_image_url || tkData.avatar_url || null,
              ownerId,
            ),
            profileUrl: username ? `https://tiktok.com/@${username}` : null,
            description: tkData.bio || null,
            icon: 'tiktok',
          });
          break;
        }
        case 'youtube': {
          const ytData = extractConnectionData<YouTubeConnectionData>(connection, 'youtube');
          const channelId = ytData.channel_id || platform_user_id;
          accounts.push({
            ...base,
            platform: 'youtube',
            displayName: connection.display_name || ytData.channel_title || 'YouTube Channel',
            identifier:
              connection.platform_username ||
              ytData.custom_url ||
              ytData.channel_title ||
              channelId,
            imageUrl: sanitizeAvatarUrl(
              connection.profile_image_url || ytData.profile_image_url || null,
              ownerId,
            ),
            profileUrl: ytData.profile_url || `https://www.youtube.com/channel/${channelId}`,
            description: ytData.description || null,
            icon: 'youtube',
          });
          break;
        }
        case 'pinterest': {
          const pinData = extractConnectionData<PinterestConnectionData>(connection, 'pinterest');
          const username = pinData.username || connection.platform_username || platform_user_id;
          accounts.push({
            ...base,
            platform: 'pinterest',
            displayName: connection.display_name || pinData.display_name || username,
            identifier: username,
            imageUrl: sanitizeAvatarUrl(
              connection.profile_image_url || pinData.profile_image_url || null,
              ownerId,
            ),
            profileUrl: username ? `https://www.pinterest.com/${username}/` : null,
            description: pinData.display_name || username || platform_user_id,
            icon: 'pinterest',
            followerCount: pinData.follower_count ?? null,
          });
          break;
        }
        case 'google-analytics': {
          const gaData = extractConnectionData<GoogleAnalyticsConnectionData>(
            connection,
            'google_analytics',
          );
          const accountEmail = getStoredConnectionEmail(connection);
          accounts.push({
            ...base,
            platform: 'google-analytics',
            displayName:
              accountEmail || connection.display_name || gaData.property_name || 'Google Analytics',
            identifier: connection.platform_username || platform_user_id,
            imageUrl: sanitizeAvatarUrl(connection.profile_image_url || null, ownerId),
            profileUrl: null,
            description: gaData.account_name || null,
            icon: 'google-analytics',
          });
          break;
        }
        case 'google-ads': {
          const adsData = extractConnectionData<GoogleAdsConnectionData>(connection, 'google_ads');
          const accountEmail = getStoredConnectionEmail(connection);
          accounts.push({
            ...base,
            platform: 'google-ads',
            displayName:
              accountEmail || connection.display_name || adsData.customer_name || 'Google Ads',
            identifier: connection.platform_username || platform_user_id,
            imageUrl: sanitizeAvatarUrl(connection.profile_image_url || null, ownerId),
            profileUrl: null,
            description: adsData.currency
              ? `Google Ads · ${adsData.currency}`
              : 'Google Ads account',
            icon: 'google-ads',
          });
          break;
        }
        case 'meta-ads': {
          const metaData = extractConnectionData<MetaAdsConnectionData>(connection, 'meta-ads');
          const accountEmail = getStoredConnectionEmail(connection);
          accounts.push({
            ...base,
            platform: 'meta-ads',
            displayName:
              accountEmail ||
              connection.display_name ||
              metaData.name ||
              metaData.business_name ||
              'Meta Ads',
            identifier:
              connection.platform_username ||
              metaData.ad_account_id ||
              platform_user_id ||
              undefined,
            imageUrl: sanitizeAvatarUrl(connection.profile_image_url || null, ownerId),
            profileUrl: null,
            description: metaData.business_name || null,
            icon: 'meta-ads',
          });
          break;
        }
        case 'slack': {
          const metadata = (connection.metadata as Record<string, unknown> | null) ?? {};
          // `platform_username` is NOT a display name for Slack: the storage RPC
          // (begin_oauth_storage_transaction_atomic_v3) sets it to
          // `COALESCE(v_account_data->>'username', v_account_id)`, and Slack's
          // additionalData never has a `username` key — so it's always just the
          // team id (e.g. "T0123ABC") restated. `display_name` is where the RPC
          // actually stores the real team name (`v_account_name`, from
          // slack-oauth-callback's auth.test `team` field). Checking
          // platform_username FIRST would show the raw team id everywhere this
          // hook's consumers render a "team name" (Settings row, the channel
          // picker dialog header) instead of the workspace's real name.
          const teamName =
            connection.display_name ||
            (typeof metadata.team_name === 'string' ? metadata.team_name : null) ||
            platform_user_id;
          accounts.push({
            ...base,
            platform: 'slack',
            displayName: teamName,
            identifier: platform_user_id,
            imageUrl: null,
            profileUrl: null,
            description: `Slack workspace: ${teamName}`,
            icon: 'slack',
          });
          break;
        }
        default:
          break;
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        logger.warn(`[PlatformIntegrations] skipped row`, err);
      }
      continue;
    }
  }
  return accounts;
}

export function usePlatformIntegrations(): UsePlatformIntegrationsReturn {
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id;
  const [isDisconnecting, setIsDisconnecting] = useState<Record<string, boolean>>({});

  // Resolve to the shared-account owner id (self, if not a team member) so
  // team members read/subscribe to the same connections the account owner
  // sees, instead of their own raw session id. See BUG-INTEGRATIONS-TEAM-001.
  const {
    data: ownerId,
    isPending: isOwnerIdPending,
    error: ownerIdError,
  } = useQuery({
    queryKey: ['current-account-owner-id', userId],
    queryFn: () => resolveOwnerId(userId ?? ''),
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Which workspace's connections to read. See `useEffectiveWorkspaceId` for the
  // RPC choice and why this key has exactly one reader definition. Mirrors
  // `useBrandKits`' `activeKit` resolution without pulling in that hook's
  // full kit-list + pointer + settings-context machinery for every one of
  // this hook's ~40 call sites.
  const { workspaceId, isPending: isWorkspacePending } = useEffectiveWorkspaceId({ ownerId });

  const {
    data: connected = [],
    isPending: isConnectionsPending,
    error: connectionsError,
  } = useQuery({
    queryKey: [...QUERY_KEY, ownerId, workspaceId],
    queryFn: () => fetchConnectedAccountsFromDb(ownerId!, workspaceId),
    enabled: Boolean(ownerId) && !isWorkspacePending,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: (failureCount, err) => {
      if (isAuthLockStealError(err)) return failureCount < 3;
      return failureCount < 1;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });

  useEffect(() => {
    // Gated on workspaceId, not just ownerId — the realtime filter below is
    // now scoped to the workspace, not the account. `user_connections` moved
    // from account-scoped (NULL brand_kit_id, shared by every workspace) to
    // workspace-scoped on 2026-08-22 (every one of the 45 production rows now
    // carries a brand_kit_id); a `user_id`-only filter kept firing
    // invalidations for OTHER workspaces' connection changes after a switch.
    if (!ownerId || !workspaceId) return;
    // Unique channel name per hook instance — Supabase otherwise returns the
    // existing channel for repeat names and rejects late .on() calls with
    // "cannot add postgres_changes callbacks ... after subscribe()".
    const channelName = `user-conn-${workspaceId}-${
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2, 10)
    }`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_connections',
          filter: `brand_kit_id=eq.${workspaceId}`,
        },
        () => {
          // Query invalidation belongs to this shared subscription. First-sync
          // notification lifecycle belongs exclusively to useOAuthConnection,
          // which polls the selected connection directly. Keeping a second
          // success toast here caused the selected-account card and success
          // toast to occupy the same bottom-right stack.
          queryClient.invalidateQueries({ queryKey: QUERY_KEY });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [ownerId, workspaceId, queryClient]);

  // CLAUDE.md "Integrations Loading Flash" rule: isLoading must stay coupled
  // to every async step feeding this hook (authLoading, and now the new
  // ownerId resolution hop) so a member never sees a premature "not
  // connected" empty state while current_account_owner_id() is in flight.
  const isPending = authLoading || isOwnerIdPending || isWorkspacePending || isConnectionsPending;

  // ownerId failure blocks the connections query from ever running (it stays
  // `enabled: false` forever), so it takes precedence — otherwise a caller
  // would only ever see the indefinite "no accounts connected" empty state
  // with no indication it's actually a fetch failure. See
  // BUG-INTEGRATIONS-TEAM-001 eng-review gap.
  const error = useMemo(() => {
    if (ownerIdError)
      return ownerIdError instanceof Error ? ownerIdError.message : String(ownerIdError);
    if (connectionsError)
      return connectionsError instanceof Error
        ? connectionsError.message
        : String(connectionsError);
    return null;
  }, [ownerIdError, connectionsError]);

  const platformsConnected = useMemo(
    () => new Set(connected.map((a) => a.platform as PlatformType)),
    [connected],
  );
  const availablePlatforms = useMemo(
    () =>
      ALL_PLATFORMS.filter(
        (platform) =>
          !platformsConnected.has(platform) &&
          (platform !== 'linkedin-ads' || linkedInAdsConnectionEnabled(import.meta.env)),
      ),
    [platformsConnected],
  );

  const disconnectMutation = useMutation({
    mutationFn: async ({ platform, accountId }: { platform: PlatformType; accountId: string }) => {
      const { data, error } = await supabase.functions.invoke('enhanced-platform-disconnect', {
        body: { platform, accountId, confirmCleanup: true },
      });
      if (error) {
        // functions-js puts a constant "non-2xx" string on error.message; the real
        // reason (e.g. the 403 owner gate) is in the JSON body on error.context.
        throw new Error(
          await ServiceError.extractEdgeFunctionMessage(
            error,
            error.message || `Failed to disconnect ${platform} account`,
          ),
        );
      }
      if (!data?.success)
        throw new Error(data?.error || `Failed to disconnect ${platform} account`);
      return {
        platform,
        accountId,
        cleanupSummary: data.cleanupSummary,
        dataDeletion: data.dataDeletion as DataDeletionReport | undefined,
      };
    },
    onMutate: ({ accountId }) => {
      setIsDisconnecting((m) => ({ ...m, [accountId]: true }));
    },
    onSuccess: ({ platform, dataDeletion }) => {
      const { tone, title, body } = describeDataDeletion(getPlatformLabel(platform), dataDeletion);
      if (tone === 'warning') showWarningToast(title, body);
      else showSuccessToast(title, body);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      showErrorToast('Disconnection failed', msg);
    },
    onSettled: (_data, _err, { accountId }) => {
      setIsDisconnecting((m) => ({ ...m, [accountId]: false }));
    },
  });

  const disconnectFunction = useCallback(
    async (platform: PlatformType, accountId: string) => {
      if (!user) {
        showErrorToast('Error', 'You must be logged in to disconnect.');
        return;
      }
      await disconnectMutation.mutateAsync({ platform, accountId });
    },
    [user, disconnectMutation],
  );

  const refreshFunction = useCallback(() => {
    // Also invalidate the ownerId resolution query so a Retry action can
    // recover from an ownerId-query failure, not just a connections-query
    // failure — see the `error` field above.
    queryClient.invalidateQueries({ queryKey: ['current-account-owner-id'] });
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    // A connect/disconnect changes more than this list: the create-wizard's
    // "Pick from Shopify" tile and the agent's useProducts default read
    // ['shopify-connection']/['shopify-products'] AND ProductService's
    // 10-minute in-memory cache. Without flushing all three, a freshly
    // connected store stayed invisible outside Settings for up to 10 minutes
    // (UAT 2026-06-12, F-081).
    queryClient.invalidateQueries({ queryKey: ['shopify-connection'] });
    queryClient.invalidateQueries({ queryKey: ['shopify-products'] });
    // F-082: lazy-import ProductService (fire-and-forget cache clear) to keep it
    // out of the root entry graph / cold-boot critical path.
    void import('@/services/data/ProductService').then(({ productService }) => {
      productService.clearShopifyCaches();
    });
  }, [queryClient]);

  const connectedPlatforms = useMemo(
    () => Array.from(new Set(connected.map((account) => account.platform))),
    [connected],
  );
  const hasShopifyConnected = useMemo(
    () => connected.some((a) => a.platform === 'shopify'),
    [connected],
  );
  const hasFacebookConnected = useMemo(
    () => connected.some((a) => a.platform === 'facebook'),
    [connected],
  );
  const hasInstagramConnected = useMemo(
    () => connected.some((a) => a.platform === 'instagram'),
    [connected],
  );
  const hasLinkedInConnected = useMemo(
    () => connected.some((a) => a.platform === 'linkedin'),
    [connected],
  );
  const hasTikTokConnected = useMemo(
    () => connected.some((a) => a.platform === 'tiktok'),
    [connected],
  );
  const hasYouTubeConnected = useMemo(
    () => connected.some((a) => a.platform === 'youtube'),
    [connected],
  );
  const hasPinterestConnected = useMemo(
    () => connected.some((a) => a.platform === 'pinterest'),
    [connected],
  );

  return {
    connectedAccounts: connected,
    availablePlatforms,
    disconnectFunction,
    isLoading: (userId ? isPending : authLoading) || Object.values(isDisconnecting).some(Boolean),
    isDisconnecting,
    refreshFunction,
    error,
    connectedPlatforms,
    hasShopifyConnected,
    hasFacebookConnected,
    hasInstagramConnected,
    hasLinkedInConnected,
    hasTikTokConnected,
    hasYouTubeConnected,
    hasPinterestConnected,
    /**
     * The active workspace `connectedAccounts` was actually scoped to —
     * `get_effective_brand_kit()`'s own resolution, exposed rather than
     * re-resolved. `useAccountConnectedSources.ts` needs this exact value to
     * scope ITS OWN `user_connections`/`analytics_fetch_runs` reads to the
     * same workspace this hook already scopes to; re-deriving it a second way
     * would risk the two disagreeing about which workspace is active.
     */
    workspaceId,
  };
}
